import { AxiosError } from 'axios';
import { FC, useMemo, useState } from 'react';

import Header from '../../../common/Header/Header';

import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { useGetClassBases } from '../../../services/base/base.service';
import { ClassBase } from '../../../services/base/base.types';
import { useGetClasses } from '../../../services/class/class.service';
import { Class } from '../../../services/class/class.types';
import { useGetOptions } from '../../../services/option/option.service';
import { Option } from '../../../services/option/option.types';
import { useGetProducts } from '../../../services/product/product.service';
import { Product } from '../../../services/product/product.types';
import { useGetRawProducts } from '../../../services/rawProd/rawProd.service';
import { RawProd } from '../../../services/rawProd/rawProd.types';
import { useGetClassSizes } from '../../../services/size/size.service';
import { ClassSize } from '../../../services/size/size.types';
import { useAddOptionsToSubrecipe } from '../../../services/subrecipe/subrecipe.service';
import { genericErrorShowToastOptions } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';

import { AddOptionFormValues, AddOptionProps, AddOptionSelectable, AddOptionFormErrorValues } from './AddOption.types';
import Confirmation from './Confirmation/Confirmation';
import Review from './Review/Review';
import SelectCharacteristics from './SelectCharacteristics/SelectCharacteristics';
import Preview from './Preview/Preview';

const AddOption: FC<AddOptionProps> = ({ specNo, setAddOptionActive }) => {
  const [step, setStep] = useState(0);
  const [formValues, setFormValues] = useState<AddOptionFormValues>({
    sourceClass: null,
    sourceBase: null,
    sourceSize: null,
    sourceProd: null,
    sourceOption: null,
    sourceRawProd: null,
    targetClass: null,
    targetBase: null,
    targetSize: null,
    targetProd: null,
    targetOption: null,
    targetRawProd: null,
  });

  const [formErrors, setFormErrors] = useState<AddOptionFormErrorValues>({
    sourceClass: '',
    sourceBase: '',
    sourceSize: '',
    sourceProd: '',
    sourceOption: '',
    sourceRawProd: '',
    targetClass: '',
    targetBase: '',
    targetSize: '',
    targetProd: '',
    targetOption: '',
    targetRawProd: '',
  });
  const {
    mutate: mutateIsReview,
    data: reviewResponse,
    reset: resetReviewMutation,
    isLoading: isReviewLoading,
  } = useAddOptionsToSubrecipe(specNo, true);

  const {
    mutate: mutateIsPreview,
    data: previewResponse,
    isLoading: isPreviewLoading,
  } = useAddOptionsToSubrecipe(specNo, false, true);

  const {
    mutate: mutateIsFinalSave,
    data: saveResponse,
    isLoading: isSaveLoading,
  } = useAddOptionsToSubrecipe(specNo, false, false);

  const handleSelect = (formId: string, option: AddOptionSelectable | null) => {
    setFormValues({ ...formValues, [formId]: option });
    if (formErrors[formId as keyof AddOptionFormValues]) {
      setFormErrors({ ...formErrors, [formId]: '' });
    }
  };

  const clearFormErrors = () => {
    setFormErrors({
      sourceClass: '',
      sourceBase: '',
      sourceSize: '',
      sourceProd: '',
      sourceOption: '',
      sourceRawProd: '',
      targetClass: '',
      targetBase: '',
      targetSize: '',
      targetProd: '',
      targetOption: '',
      targetRawProd: '',
    });
  }

  const handleSourceClassSelect = (option: AddOptionSelectable | null) => {
    setFormValues(prevState => ({
      sourceClass: option,
      sourceBase: null,
      sourceSize: null,
      sourceProd: null,
      sourceOption: null,
      sourceRawProd: null,
      targetClass: prevState.targetClass,
      targetBase: null,
      targetSize: null,
      targetProd: null,
      targetOption: null,
      targetRawProd: null,
    }));
    setFormErrors({...formErrors, sourceClass: ''});
  }

  const validateFormValues = () => {
    let error = false;
    let newFormErrors = {
      sourceClass: '',
      sourceBase: '',
      sourceSize: '',
      sourceProd: '',
      sourceOption: '',
      sourceRawProd: '',
      targetClass: '',
      targetBase: '',
      targetSize: '',
      targetProd: '',
      targetOption: '',
      targetRawProd: '',
    };
    if (formValues.sourceClass === null) {
      error = true;
      newFormErrors.sourceClass = 'Source Class is required.';
    }
    if (formValues.targetClass === null) {
      error = true;
      newFormErrors.targetClass = 'Target Class is required.';
    }
    if ((formValues.sourceBase !== null && formValues.targetBase === null)) {
      error = true;
      newFormErrors.targetBase = 'Target Base is required if Source Base is present.';
    }
    if ((formValues.sourceSize !== null && formValues.targetSize === null)) {
      error = true;
      newFormErrors.targetSize = 'Target Size is required if Source Size is present.';
    }
    if ((formValues.sourceProd !== null && formValues.targetProd === null)) {
      error = true;
      newFormErrors.targetProd = 'Target Prod is required if Source Prod is present.';
    }
    if (formValues.sourceClass?.value !== formValues.targetClass?.value) {
      if (formValues.sourceOption === null) {
        error = true;
        newFormErrors.sourceOption = 'Source Option is required if Source Class is different from Target Class.';
      }
      if (formValues.targetOption === null) {
        error = true;
        newFormErrors.targetOption = 'Target Option is required if Source Class is different from Target Class.';
      }
    }
    if (formValues.sourceOption !== null && formValues.targetOption === null) {
      error = true;
      newFormErrors.targetOption = 'Target Option is required if Source Option is present.';
    }
    if (formValues.sourceRawProd !== null && formValues.targetRawProd === null) {
      error = true;
      newFormErrors.targetRawProd = 'Target Raw Product is required if Source Raw Product is present.';
    }
    if (error) {
      setFormErrors(newFormErrors);
    }
    return error;
  };

  const handleReviewClick = () => {
    const error = validateFormValues();
    if (!error) {
      clearFormErrors();
      mutateIsReview(
        {
          sourceClassNo: formValues.sourceClass?.value,
          sourceBaseNo: formValues.sourceBase?.value,
          sourceSizeNo: formValues.sourceSize?.value,
          sourceProdNo: formValues.sourceProd?.value,
          sourceOptionNo: formValues.sourceOption?.value,
          sourceRawProdNo: formValues.sourceRawProd?.value,
          targetClassNo: formValues.targetClass?.value,
          targetBaseNo: formValues.targetBase?.value,
          targetSizeNo: formValues.targetSize?.value,
          targetProdNo: formValues.targetProd?.value,
          targetOptionNo: formValues.targetOption?.value,
          targetRawProdNo: formValues.targetRawProd?.value,
        },
        {
          onSuccess: () => {
            setStep(1);
          },
          onError: (error, variables, context) => {
            if ((error as AxiosError).message === 'Request failed with status code 404') {
              showToast({
                label: 'No specs exist with those Source Characteristics.',
                subtext: 'Please change your selections.',
                id: 'AddOptionReview404',
                toastType: ToastTypes.Info,
              });
              resetReviewMutation();
            } else if ((error as AxiosError).message === 'apiGatewayTimeout') {
              return;
            } else {
              showToast({ ...genericErrorShowToastOptions, label: 'Error fetching subspecs' });
            }
          },
        },
      );
    }
  };
  const handlePreviewClick = () => {
    mutateIsPreview(
      {
        sourceClassNo: formValues.sourceClass?.value,
        sourceBaseNo: formValues.sourceBase?.value,
        sourceSizeNo: formValues.sourceSize?.value,
        sourceProdNo: formValues.sourceProd?.value,
        sourceOptionNo: formValues.sourceOption?.value,
        sourceRawProdNo: formValues.sourceRawProd?.value,
        targetClassNo: formValues.targetClass?.value,
        targetBaseNo: formValues.targetBase?.value,
        targetSizeNo: formValues.targetSize?.value,
        targetProdNo: formValues.targetProd?.value,
        targetOptionNo: formValues.targetOption?.value,
        targetRawProdNo: formValues.targetRawProd?.value,
      },
      {
        onSuccess: (_saveResponse) => {
          setStep(2);
        },
        onError: (error, variables, context) => {
          if ((error as AxiosError).message === 'apiGatewayTimeout') return;
          showToast({ label: 'Error adding subspecs', toastType: ToastTypes.Error, id: 'AddOptionError' });
        },
      },
    );
  };

  const handleSaveClick = () => {
    mutateIsFinalSave(
      {
        sourceClassNo: formValues.sourceClass?.value,
        sourceBaseNo: formValues.sourceBase?.value,
        sourceSizeNo: formValues.sourceSize?.value,
        sourceProdNo: formValues.sourceProd?.value,
        sourceOptionNo: formValues.sourceOption?.value,
        sourceRawProdNo: formValues.sourceRawProd?.value,
        targetClassNo: formValues.targetClass?.value,
        targetBaseNo: formValues.targetBase?.value,
        targetSizeNo: formValues.targetSize?.value,
        targetProdNo: formValues.targetProd?.value,
        targetOptionNo: formValues.targetOption?.value,
        targetRawProdNo: formValues.targetRawProd?.value,
      },
      {
        onSuccess: (saveResponse) => {
          setStep(3);
          if (!saveResponse.data.length)
            showToast({
              label: 'Subspecs already exist with target characteristics.',
              toastType: ToastTypes.Error,
              id: 'AddOptionDuplicate',
            });
          else
            showToast({ label: 'Subspecs added successfully.', toastType: ToastTypes.Success, id: 'AddOptionSuccess' });
        },
        onError: (error, variables, context) => {
          if ((error as AxiosError).message === 'apiGatewayTimeout') return;
          showToast({ label: 'Error adding subspecs', toastType: ToastTypes.Error, id: 'AddOptionError' });
        },
      },
    );
  };

  // covered in a future story
  // const handleChangeUsageClick = () => {
  //   // TODO  -whatever this does
  // };

  // fetch active classes and map to dropdown options
  const { data: classData } = useGetClasses(0);
  const classOptions: AddOptionSelectable[] =
    classData?.data?.map((item: Class) => {
      return { value: item?.classNo, label: `${item?.description} ~ ${item.classNo}` };
    }) || [];

  // fetch bases and map to dropdown options
  const { data: baseData } = useGetClassBases(
    { classNo: formValues.sourceClass?.value?.toString(), status: 0 },
    formValues.sourceClass !== null,
  );

  const baseOptions: AddOptionSelectable[] = useMemo(() => {
    if (baseData?.data) {
      let basesOptions: AddOptionSelectable[] = baseData?.data?.map((base: ClassBase) => {
        return { value: base?.baseNo, label: `${base?.baseData?.description} ~ ${base.baseNo}` };
      });
      return basesOptions;
    } else {
      return [];
    }
  }, [formValues.sourceClass, specNo, baseData]);


  // fetch sizes and map to dropdown options
  const { data: sizeData } = useGetClassSizes(
    { classNo: formValues.sourceClass?.value?.toString(), status: 0 },
    formValues.sourceClass !== null,
  );

  const sizeOptions: AddOptionSelectable[] = useMemo(() => {
    if (sizeData?.data) {
      const sizeOptions: AddOptionSelectable[] = sizeData?.data?.map((item: ClassSize) => {
        return { value: item?.sizeNo, label: `${item?.sizeData?.description} ~ ${item.sizeNo}` };
      });
      return sizeOptions;
    } else {
      return [];
    }
  }, [formValues.sourceClass, specNo, sizeData]);

  // fetch products and map to dropdown options
  const { data: productData } = useGetProducts({
    classNo: formValues.sourceClass?.value ?? undefined,
    status: 0,
  }, formValues.sourceClass !== null);

  const productOptions: AddOptionSelectable[] = useMemo(() => {
    if (productData?.data) {
      const productOptions: AddOptionSelectable[] = productData?.data?.map((item: Product) => {
        return { value: item?.productNo, label: `${item?.description} ~ ${item.productNo}` };
      });
      return productOptions;
    } else {
      return [];
    }
  }, [formValues.sourceClass, specNo, productData]);

  // fetch options and map to dropdown options
  const { data: optionData } = useGetOptions({
    classNo: formValues.sourceClass?.value?.toString(),
    statusNo: '0',
  }, formValues.sourceClass !== null);

  const optionOptions: AddOptionSelectable[] = useMemo(() => {
    if (optionData?.data) {
      const optionOptions: AddOptionSelectable[] = optionData?.data?.map((item: Option) => {
        return { value: item?.optionNo, label: `${item?.description} ~ ${item.optionNo}` };
      });
      return optionOptions;
    } else {
      return [];
    }
  }, [formValues.sourceClass, specNo, optionData]);

  // fetch rawProds and map to dropdown options
  const { data: rawProdData } = useGetRawProducts(true);

  const rawProdOptions: AddOptionSelectable[] = useMemo(() => {
    if (rawProdData?.data) {
      const rawProdOptions: AddOptionSelectable[] = rawProdData?.data?.map((item: RawProd) => {
        return { value: item?.moaRawProdNo, label: `${item?.moaRawProdDesc} ~ ${item.moaRawProdNo}` };
      });
      return rawProdOptions;
    } else {
      return [];
    }
  }, [formValues.sourceClass, specNo, rawProdData]);

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <SelectCharacteristics
            formValues={formValues}
            formErrors={formErrors}
            setStep={setStep}
            handleSelect={handleSelect}
            handleReviewClick={handleReviewClick}
            handleSourceClassSelect={handleSourceClassSelect}
            classOptions={classOptions}
            baseOptions={baseOptions}
            sizeOptions={sizeOptions}
            productOptions={productOptions}
            optionOptions={optionOptions}
            rawProdOptions={rawProdOptions}
            isLoading={isReviewLoading}
          />
        );
      case 1:
        return (
          <Review
            handleSaveClick={handlePreviewClick}
            reviewResponse={reviewResponse?.data || []}
            setStep={setStep}
            isLoading={isPreviewLoading}
          />
        );
      case 2:
        return (
          <Preview
            handleSaveClick={handleSaveClick}
            previewResponse={previewResponse?.data || []}
            setStep={setStep}
            isLoading={isSaveLoading}
          />
        );
      case 3:
        return <Confirmation saveResponse={saveResponse?.data || []} setStep={setStep} />;
    }
  };

  return (
    <>
      <Header
        breadcrumb={{ label: 'Back to Spec', to: `/optionrecipes/${specNo}`, onClick: () => setAddOptionActive(false) }}
        title={`Add Option - Spec ${specNo}`}
        stepTracker={
          step !== 3
            ? {
              steps: [
                { label: 'Select Source & Target', onClick: () => setStep(0) },
                { label: 'Review', onClick: () => setStep(1) },
                { label: 'Preview', onClick: () => setStep(2) },
              ],
              currentStep: step,
            }
            : undefined
        }
      />
      {renderStep()}
    </>
  );
};

export default AddOption;
